import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, btnPrimary, btnSecondary } from '../styles/theme.js'
import {
  sessions as sessionsApi, tags as tagsApi, projects as projectsApi,
  evaluations as evalsApi, similarity as similarityApi,
  sessionRubrics as sessionRubricsApi,
} from '../lib/api.js'
import { ChatRenderer } from '../prosemirror/chat-renderer.js'
import { PropagateEvalPanel } from '../components/propagate-eval-panel.js'
import { buildDefaultGrades, mergeExistingGrades } from '../components/eval-panel.js'

// --- Styles ---

const wrap = css`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  overflow: hidden;
`

const topBar = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  background: ${colors.bgCard};
  flex-shrink: 0;
  flex-wrap: wrap;
`

const filterBar = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.xs} ${space.md};
  border-bottom: 1px solid ${colors.border};
  background: ${colors.bg};
  flex-shrink: 0;
  flex-wrap: wrap;
  font-size: ${font.size.xs};
`

const panels = css`
  display: flex;
  flex: 1;
  overflow: hidden;
`

const chatPanel = css`
  flex: 1;
  overflow-y: auto;
  border-right: 1px solid ${colors.border};
  min-width: 0;
`

const evalPanel = css`
  width: 420px;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const navBtn = css`
  ${btnSecondary};
  font-size: ${font.size.xs};
  padding: ${space.xs} ${space.sm};
`

const saveNavBtn = css`
  ${btnPrimary};
  font-size: ${font.size.xs};
  padding: ${space.xs} ${space.sm};
`

const labelStyle = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
`

const similarityBadge = css`
  font-size: ${font.size.xs};
  padding: 2px 8px;
  border-radius: 10px;
  background: ${colors.brand}22;
  color: ${colors.brand};
  font-weight: ${font.weight.medium};
`

const tagChip = css`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: ${font.size.xs};
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
`

const tagRemoveBtn = css`
  border: none;
  background: none;
  color: ${colors.textMuted};
  cursor: pointer;
  font-size: 11px;
  padding: 0 2px;
  line-height: 1;
  &:hover { color: ${colors.brand}; }
`

const addTagBtn = css`
  padding: 2px 8px;
  border-radius: 10px;
  font-size: ${font.size.xs};
  border: 1px dashed ${colors.border};
  color: ${colors.textMuted};
  cursor: pointer;
  background: none;
  &:hover { border-color: ${colors.brand}; color: ${colors.text}; }
`

const tagDropdown = css`
  position: absolute;
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  min-width: 150px;
`

const tagDropdownItem = css`
  padding: ${space.xs} ${space.sm};
  font-size: ${font.size.xs};
  cursor: pointer;
  &:hover { background: ${colors.bg}; }
`

const progressBar = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.xs} ${space.md};
  border-top: 1px solid ${colors.border};
  background: ${colors.bg};
  flex-shrink: 0;
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
`

const progressTrack = css`
  flex: 1;
  height: 6px;
  background: ${colors.border};
  border-radius: 3px;
  overflow: hidden;
`

const progressFill = css`
  height: 100%;
  background: ${colors.brand};
  border-radius: 3px;
  transition: width 0.2s ease;
`

const emptyState = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${colors.textMuted};
  font-style: italic;
  font-size: ${font.size.sm};
  padding: ${space.lg};
  text-align: center;
`

const checkboxLabel = css`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  cursor: pointer;
`

// PropagateView — dedicated view for propagating evaluations from a source session
// to similar sessions. Two-panel layout: chat on left, grades on right.
//
// Route: /projects/:projectId/propagate/:sourceSessionId
export function PropagateView() {
  let projectId = null
  let sourceSessionId = null
  let loadController = null

  // Source data
  let sourceSession = null
  let sourceEvalData = []  // [{ evaluation, rubric }]

  // Target list
  let targets = []         // [{ sessionId, externalId, similarity, ... }]
  let currentIndex = 0

  // Filters
  let allTags = []
  let filterTagIds = []
  let excludeEvaluated = true
  let showTagDropdown = false

  // Current target state
  let targetDetail = null  // { session, highlights, comments, ... }
  let targetExistingEvals = []
  let grades = {}          // rubricId -> { criterionId: { fulfilled, notes } }
  let sourceGrades = {}    // rubricId -> { criterionId: { fulfilled, notes } } (snapshot)
  let autoFilledMap = {}   // rubricId -> { criterionId: boolean }
  let dirty = false
  let saving = false

  // Progress
  let reviewed = new Set()
  let skipped = new Set()

  // Loading states
  let loadingSource = true
  let loadingTargets = true
  let loadingTarget = false
  let error = null

  function abort() {
    if (loadController) { loadController.abort(); loadController = null }
  }

  function newController() {
    abort()
    loadController = new AbortController()
    return loadController.signal
  }

  async function loadSourceData(signal) {
    loadingSource = true
    error = null
    m.redraw()
    try {
      const [detail, evalData, proj] = await Promise.all([
        sessionsApi.getDetail(projectId, sourceSessionId, { signal }),
        evalsApi.getSourceData(projectId, sourceSessionId),
        projectsApi.get(projectId, { signal }),
      ])
      sourceSession = detail.session
      sourceEvalData = evalData

      // Check user role
      const userRole = proj.memberRole || 'viewer'
      if (userRole !== 'owner' && userRole !== 'editor') {
        error = 'You need editor or owner role to propagate evaluations.'
        loadingSource = false
        m.redraw()
        return
      }

      if (sourceEvalData.length === 0) {
        error = 'Source session has no evaluations. Grade the source session first.'
        loadingSource = false
        m.redraw()
        return
      }

      // Build source grades snapshot
      for (const { evaluation, rubric } of sourceEvalData) {
        const criteria = rubric.criteria || []
        let g = buildDefaultGrades(criteria)
        g = mergeExistingGrades(g, evaluation.grades || [])
        sourceGrades[rubric.id] = g
      }

      loadingSource = false
      m.redraw()
    } catch (err) {
      if (err.name === 'AbortError') return
      error = err.message || 'Failed to load source data'
      loadingSource = false
      m.redraw()
    }
  }

  async function loadTags(signal) {
    try {
      allTags = await tagsApi.list(projectId, { signal })
    } catch (err) {
      if (err.name === 'AbortError') return
      allTags = []
    }
    m.redraw()
  }

  async function loadTargets() {
    loadingTargets = true
    m.redraw()
    try {
      const params = { limit: 50, excludeEvaluated: String(excludeEvaluated) }
      if (filterTagIds.length > 0) {
        params.tagIds = filterTagIds.join(',')
      }
      targets = await similarityApi.findSimilarFiltered(projectId, sourceSessionId, params)
      currentIndex = 0
      loadingTargets = false
      m.redraw()
      if (targets.length > 0) {
        await loadCurrentTarget()
      }
    } catch (err) {
      if (err.name === 'AbortError') return
      targets = []
      loadingTargets = false
      m.redraw()
    }
  }

  async function loadCurrentTarget() {
    if (currentIndex < 0 || currentIndex >= targets.length) return
    const target = targets[currentIndex]
    loadingTarget = true
    dirty = false
    m.redraw()

    try {
      const [detail, existingEvals] = await Promise.all([
        sessionsApi.getDetail(projectId, target.sessionId),
        evalsApi.list(projectId, target.sessionId),
      ])
      targetDetail = detail
      targetExistingEvals = existingEvals || []

      // Pre-fill grades from source, but reset if version mismatch
      grades = {}
      autoFilledMap = {}
      for (const { evaluation, rubric } of sourceEvalData) {
        const criteria = rubric.criteria || []
        const sourceVersionId = evaluation.rubric_version_id
        const currentVersionId = rubric.current_version_id

        let g = buildDefaultGrades(criteria)
        const af = {}
        for (const c of criteria) { af[c.id] = false }

        if (sourceVersionId === currentVersionId) {
          // Same version — copy source grades
          g = mergeExistingGrades(g, evaluation.grades || [])
          for (const eg of (evaluation.grades || [])) {
            const cid = eg.criteria_id || eg.id
            if (cid in af) af[cid] = true
          }
        }
        // If version mismatch, leave as ungraded (user must re-evaluate)

        // Check if target already has grades for this rubric and merge those instead
        const targetEval = targetExistingEvals.find(e => e.rubric_id === rubric.id)
        if (targetEval) {
          g = mergeExistingGrades(g, targetEval.grades || [])
          // Target grades are human-evaluated, not auto-filled
          for (const tg of (targetEval.grades || [])) {
            const cid = tg.criteria_id || tg.id
            if (cid in af) af[cid] = false
          }
        }

        grades[rubric.id] = g
        autoFilledMap[rubric.id] = af
      }

      dirty = false
      loadingTarget = false
      m.redraw()
    } catch (err) {
      if (err.name === 'AbortError') return
      loadingTarget = false
      m.redraw()
    }
  }

  function setGrade(rubricId, criterionId, fulfilled) {
    if (!grades[rubricId]) return
    grades[rubricId][criterionId] = { ...grades[rubricId][criterionId], fulfilled }
    if (autoFilledMap[rubricId]) autoFilledMap[rubricId][criterionId] = false
    dirty = true
  }

  function setNotes(rubricId, criterionId, notes) {
    if (!grades[rubricId]) return
    grades[rubricId][criterionId] = { ...grades[rubricId][criterionId], notes }
    if (autoFilledMap[rubricId]) autoFilledMap[rubricId][criterionId] = false
    dirty = true
  }

  async function saveAndAdvance() {
    if (saving || currentIndex >= targets.length) return
    saving = true
    m.redraw()

    try {
      const target = targets[currentIndex]
      for (const { rubric } of sourceEvalData) {
        // Ensure rubric is applied to target session
        try {
          await sessionRubricsApi.add(projectId, target.sessionId, rubric.id)
        } catch {
          // Ignore — rubric may already be applied (INSERT OR IGNORE on server)
        }

        // Save evaluation
        const rubricGrades = grades[rubric.id] || {}
        const payload = {
          rubricId: rubric.id,
          rubricVersionId: rubric.current_version_id,
          grades: Object.entries(rubricGrades).map(([criteriaId, g]) => ({
            criteriaId,
            fulfilled: g.fulfilled,
            notes: g.notes || undefined,
          })),
        }
        await evalsApi.save(projectId, target.sessionId, payload)
      }

      reviewed.add(target.sessionId)
      advance()
    } catch (err) {
      console.error('Failed to save evaluation:', err)
    } finally {
      saving = false
      m.redraw()
    }
  }

  function skipAndAdvance() {
    if (currentIndex >= targets.length) return
    skipped.add(targets[currentIndex].sessionId)
    advance()
  }

  function advance() {
    if (currentIndex < targets.length - 1) {
      currentIndex++
      loadCurrentTarget()
    }
    m.redraw()
  }

  function goBack() {
    if (currentIndex > 0) {
      currentIndex--
      loadCurrentTarget()
    }
  }

  function handleKeydown(e) {
    // Don't capture when typing in inputs
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return

    if (e.key === 'Escape') {
      m.route.set(`/projects/${projectId}/sessions/${sourceSessionId}`)
    } else if (e.key === 'Enter' || (e.ctrlKey && e.key === 's')) {
      e.preventDefault()
      saveAndAdvance()
    } else if (e.key === 'ArrowRight' || e.key === 'j') {
      skipAndAdvance()
    } else if (e.key === 'ArrowLeft' || e.key === 'k') {
      goBack()
    }
  }

  function toggleTagFilter(tagId) {
    const idx = filterTagIds.indexOf(tagId)
    if (idx >= 0) {
      filterTagIds.splice(idx, 1)
    } else {
      filterTagIds.push(tagId)
    }
    showTagDropdown = false
    loadTargets()
  }

  function toggleExcludeEvaluated() {
    excludeEvaluated = !excludeEvaluated
    loadTargets()
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      sourceSessionId = vnode.attrs.sourceSessionId

      const signal = newController()
      Promise.all([
        loadSourceData(signal),
        loadTags(signal),
      ]).then(() => {
        if (!error) loadTargets()
      })
    },

    oncreate() {
      document.addEventListener('keydown', handleKeydown)
    },

    onremove() {
      document.removeEventListener('keydown', handleKeydown)
      abort()
    },

    view() {
      if (loadingSource) {
        return m('div', { class: wrap },
          m('div', { class: emptyState }, 'Loading source session...'))
      }

      if (error) {
        return m('div', { class: wrap }, [
          m('div', { class: topBar }, [
            m('button', {
              class: navBtn,
              onclick: () => m.route.set(`/projects/${projectId}/sessions/${sourceSessionId}`),
            }, 'Back'),
          ]),
          m('div', { class: emptyState }, error),
        ])
      }

      const currentTarget = targets[currentIndex]
      const totalReviewed = reviewed.size
      const totalSkipped = skipped.size
      const total = targets.length
      const progressPct = total > 0 ? ((totalReviewed + totalSkipped) / total * 100) : 0

      const content = []

      // --- Top bar ---
      const topItems = []
      topItems.push(m('button', {
        key: 'back',
        class: navBtn,
        onclick: () => m.route.set(`/projects/${projectId}/sessions/${sourceSessionId}`),
        title: 'Back to source session (Esc)',
      }, 'Back'))
      topItems.push(m('span', { key: 'source', class: labelStyle },
        `Source: ${sourceSession?.external_id || sourceSessionId.slice(0, 12)}`))

      if (total > 0) {
        topItems.push(m('span', { key: 'progress', class: labelStyle },
          `${totalReviewed}/${total} reviewed`))
        if (currentTarget) {
          topItems.push(m('span', { key: 'sim', class: similarityBadge },
            `${Math.round(currentTarget.similarity * 100)}% similar`))
        }

        topItems.push(m('span', { key: 'spacer', style: 'flex: 1;' }))

        topItems.push(m('button', {
          key: 'prev',
          class: navBtn,
          disabled: currentIndex <= 0,
          onclick: goBack,
          title: 'Previous (← or k)',
        }, '\u2190 Prev'))
        topItems.push(m('button', {
          key: 'skip',
          class: navBtn,
          onclick: skipAndAdvance,
          disabled: currentIndex >= total - 1,
          title: 'Skip (→ or j)',
        }, 'Skip'))
        topItems.push(m('button', {
          key: 'save',
          class: saveNavBtn,
          disabled: saving,
          onclick: saveAndAdvance,
          title: 'Save & Next (Enter)',
        }, saving ? 'Saving...' : 'Save & Next \u2192'))
      }

      content.push(m('div', { key: 'top', class: topBar }, topItems))

      // --- Filter bar ---
      const filterItems = []
      filterItems.push(m('span', { key: 'label' }, 'Filters:'))

      for (const tagId of filterTagIds) {
        const tag = allTags.find(t => t.id === tagId)
        if (tag) {
          filterItems.push(m('span', { key: tagId, class: tagChip }, [
            tag.name,
            m('button', {
              class: tagRemoveBtn,
              onclick: () => toggleTagFilter(tagId),
            }, '\u00d7'),
          ]))
        }
      }

      filterItems.push(m('span', { key: 'add-wrap', style: 'position: relative; display: inline-block;' }, [
        m('button', {
          class: addTagBtn,
          onclick: () => { showTagDropdown = !showTagDropdown },
        }, '+ Tag'),
        showTagDropdown ? m('div', { class: tagDropdown },
          allTags
            .filter(t => !filterTagIds.includes(t.id))
            .map(t => m('div', {
              key: t.id,
              class: tagDropdownItem,
              onclick: () => toggleTagFilter(t.id),
            }, t.name))
        ) : null,
      ]))

      filterItems.push(m('label', { key: 'excl', class: checkboxLabel }, [
        m('input', {
          type: 'checkbox',
          checked: excludeEvaluated,
          onchange: toggleExcludeEvaluated,
        }),
        'Exclude already evaluated',
      ]))

      content.push(m('div', { key: 'filter', class: filterBar }, filterItems))

      // --- Main panels ---
      if (loadingTargets) {
        content.push(m('div', { key: 'loading', class: emptyState }, 'Finding similar sessions...'))
      } else if (targets.length === 0) {
        content.push(m('div', { key: 'empty', class: emptyState },
          'No similar sessions found. Try adjusting filters or generate embeddings for more sessions.'))
      } else if (loadingTarget) {
        content.push(m('div', { key: 'panels', class: panels }, [
          m('div', { class: chatPanel },
            m('div', { class: emptyState }, 'Loading session...')),
          m('div', { class: evalPanel },
            m('div', { class: emptyState }, 'Loading...')),
        ]))
      } else {
        const messages = targetDetail?.session?.messages
          ? (typeof targetDetail.session.messages === 'string'
            ? JSON.parse(targetDetail.session.messages)
            : targetDetail.session.messages)
          : []

        content.push(m('div', { key: 'panels', class: panels }, [
          m('div', { class: chatPanel }, [
            m('div', {
              style: `padding: ${space.xs} ${space.md}; font-size: ${font.size.xs}; color: ${colors.textMuted}; border-bottom: 1px solid ${colors.border};`,
            }, `Target: ${currentTarget?.externalId || targets[currentIndex]?.sessionId?.slice(0, 12)} (${currentIndex + 1} of ${total})`),
            m(ChatRenderer, {
              messages,
              highlights: targetDetail?.highlights || [],
            }),
          ]),
          m('div', { class: evalPanel },
            m(PropagateEvalPanel, {
              sourceEvalData,
              grades,
              sourceGrades,
              autoFilledMap,
              onGradeChange: setGrade,
              onNotesChange: setNotes,
              onSave: saveAndAdvance,
              onSkip: skipAndAdvance,
              saving,
              dirty,
              targetHasExistingEvals: targetExistingEvals.length > 0,
            }),
          ),
        ]))
      }

      // --- Progress bar ---
      if (total > 0) {
        content.push(m('div', { key: 'progress', class: progressBar }, [
          m('div', { class: progressTrack },
            m('div', { class: progressFill, style: `width: ${progressPct}%;` })),
          m('span', `${totalReviewed} reviewed, ${totalSkipped} skipped — ${total} total`),
          m('span', { style: `margin-left: ${space.sm}; font-size: 10px; color: ${colors.textMuted};` },
            'Keys: ←/k prev · →/j skip · Enter save · Esc exit'),
        ]))
      }

      return m('div', { class: wrap }, content)
    },
  }
}
